import stripHtml from 'lib/strip-html'
import capitalize from 'lodash.capitalize'

const toAPAAuthors = paper => {
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map(author => {
    let family = stripHtml(author?.family)
    let initials = author?.given?.split(' ')
      .map(name => stripHtml(name).slice(0, 1) + '.')
      .join(' ')

    if (family && initials) {
      return `${family}, ${initials}`
    } else if (family) {
      return family
    }
    if (author?.authorName) {
      // grab last name from author formatted like "Domenic Rosati"
      const nameParts = stripHtml(author?.authorName).split(' ')
      if (nameParts.length > 1) {
        family = nameParts[nameParts.length - 1]
        // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
        if (nameParts[nameParts.length - 1] === '.') {
          family = nameParts[nameParts.length - 2]
        }
        initials = `${nameParts[0][0]}.`
        return `${family}, ${initials}`
      }
    }

    return ''
  })?.filter(author => !!author)

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' and ')
  }

  if (authorStrings.length <= 6) {
    // join the last two authors with an ampersand
    return authorStrings.slice(0, -1).join(', ') + ', & ' + authorStrings.slice(-1)[0]
  }

  return authorStrings.slice(0, 6).join(', ') + ', … & ' + authorStrings.slice(-1)[0]
}

export const toAPAReference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toAPAAuthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = ''

  if (authors) {
    result += authors
  }

  if (year) {
    result += ` (${year}).`
  }

  result += ` ${title}.`

  if (journal) {
    result += ` ${journal}`
  }

  if (volume) {
    result += `, ${volume}`
  } else if (issue) {
    result += ', '
  }

  if (issue) {
    result += `(${issue})`
  }

  if (page) {
    result += `, ${page}`
  }

  result += `. https://doi.org/${paper.doi}`

  return stripHtml(result)
}

export const toAPAAuthorsInText = paper => {
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map(author => {
    if (!author) {
      return ''
    }
    if (author?.authorName) {
      const nameParts = stripHtml(author?.authorName).split(' ')
      // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
      if (nameParts[nameParts.length - 1] === '.') {
        return nameParts[nameParts.length - 2]
      }
      return nameParts[nameParts.length - 1]
    }
    if (author?.family) {
      return stripHtml(author?.family)
    }

    return ''
  })?.filter(author => !!author)

  if (!authorStrings || authorStrings.length === 0) {
    return ''
  }

  if (authorStrings.length === 1) {
    return authorStrings[0] || ''
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' & ')
  }
  return authorStrings[0] + ' et al.'
}

export const toAPAInTextCitation = (paper, useParenthesis = false) => {
  if (!paper) {
    return ''
  }

  let authors = toAPAAuthorsInText(paper)?.trim() || ''
  const year = paper.year
  if (!authors) {
    authors = `"${paper.title}"`
  }
  if (useParenthesis && year) {
    return `(${authors}, ${year})`
  } else if (useParenthesis && !year) {
    return `(${authors}, n.d.)`
  }
  if (year) {
    return `${authors} (${year})`
  }
  return `${authors} (n.d.)`
}

const convertMLAMiddleName = givenName => {
  if (!givenName.includes(' ')) {
    return givenName
  }
  const parts = givenName.split(' ')
  const first = parts[0]
  if (first.endsWith('.')) {
    return givenName
  }
  const middleInitial = parts[1][0]
  return `${first} ${middleInitial}.`
}

const toMLAauthors = paper => {
  if (!paper?.authors) {
    return
  }
  const firstAuthor = paper?.authors[0]
  let family = stripHtml(firstAuthor?.family)
  let given = stripHtml(firstAuthor?.given)

  if (family && given) {
    return `${family}, ${convertMLAMiddleName(given)}, et al.`
  } else if (family) {
    return `${family} et al.`
  }
  if (firstAuthor?.authorName) {
    const nameParts = stripHtml(firstAuthor?.authorName).split(' ')
    family = nameParts[nameParts.length - 1]
    // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
    if (nameParts[nameParts.length - 1] === '.') {
      family = nameParts[nameParts.length - 2]
    }
    given = `${nameParts[0]}`

    return `${family}, ${convertMLAMiddleName(given)}, et al.`
  }
  return ''
}

export const toMLAreference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toMLAauthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = ''

  if (authors) {
    result += authors
  }

  result += ` "${title}".`

  if (journal) {
    result += ` ${journal}`
  }

  if (volume) {
    result += `, vol. ${volume}`
  }

  if (issue) {
    result += `, no. ${issue}`
  }

  if (year) {
    result += `, ${year}`
  }

  if (page) {
    result += `, p. ${page}`
  }

  result += `. https://doi.org/${paper.doi}`

  return stripHtml(result)
}

export const toMLAInTextCitation = (paper, useParenthesis) => {
  if (!paper) {
    return ''
  }

  let authors = toAPAAuthorsInText(paper)?.trim() || ''
  if (!authors) {
    authors = `"${paper.title}"`
  }
  if (!useParenthesis) {
    return authors
  }

  const page = paper.page
  if (page) {
    return `(${authors} ${page})`
  }
  return `(${authors})`
}

export const toIEEEauthors = paper => {
  // The author's name is listed as first initial, last name.
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map(author => {
    let family = stripHtml(author?.family)
    let initials = author?.given?.split(' ')
      .map(name => stripHtml(name).slice(0, 1) + '.')[0]

    if (family && initials) {
      return `${initials} ${family}`
    } else if (family) {
      return family
    }

    if (author?.authorName) {
      // grab last name from author formatted like "Domenic Rosati"
      const nameParts = stripHtml(author?.authorName).split(' ')
      if (nameParts.length > 1) {
        family = nameParts[nameParts.length - 1]
        // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
        if (nameParts[nameParts.length - 1] === '.') {
          family = nameParts[nameParts.length - 2]
        }
        initials = `${nameParts[0][0]}.`
        return `${initials} ${family}`
      }
    }

    return ''
  })?.filter(author => !!author)

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' and ')
  }

  if (authorStrings.length <= 6) {
    // join the last two authors with an ampersand
    return authorStrings.slice(0, -1).join(', ') + ', & ' + authorStrings.slice(-1)[0]
  }

  return authorStrings.slice(0, 6).join(', ') + 'et al.'
}

export const toIEEEreference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toIEEEauthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = ''

  if (authors) {
    result += authors
  }

  if (title) {
    result += `, "${title}",`
  }

  if (journal) {
    result += ` ${journal}`
  }

  if (volume) {
    result += `, vol. ${volume}`
  }

  if (issue) {
    result += `, no. ${issue}`
  }

  if (page) {
    result += `, p. ${page}`
  }

  if (year) {
    result += `, ${year}`
  }

  result += '. https://doi.org/' + paper.doi

  return stripHtml(result)
}

export const toIEEEInTextCitation = (paper, useParenthesis, referenceNumber) => {
  const refNumStyle = `[${referenceNumber}]`
  if (!paper) {
    return refNumStyle
  }

  if (useParenthesis) {
    return refNumStyle
  }

  const authors = toAPAAuthorsInText(paper)?.trim() || ''
  if (authors) {
    return authors + ' ' + refNumStyle
  }
  return refNumStyle
}

export const toChicagoAuthorsInText = paper => {
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map((author, i) => {
    if (author?.family) {
      return author?.family
    }

    if (author?.authorName) {
      // grab last name from author formatted like "Domenic Rosati"
      const nameParts = stripHtml(author?.authorName).split(' ')
      if (nameParts.length > 1) {
        let family = nameParts[nameParts.length - 1]
        // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
        if (nameParts[nameParts.length - 1] === '.') {
          family = nameParts[nameParts.length - 2]
        }
        return `${family}`
      }
    }
    return ''
  })

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' and ')
  }

  if (authorStrings.length <= 4) {
    return authorStrings.slice(0, -1).join(', ') + ', and ' + authorStrings.slice(-1)[0]
  }

  return authorStrings[0] + ' et al.'
}

export const toChicagoInTextCitation = (paper, useParenthesis) => {
  if (!paper) {
    return ''
  }

  let authors = toChicagoAuthorsInText(paper)?.trim() || ''
  if (!authors) {
    authors = `"${paper.title}"`
  }
  if (!useParenthesis) {
    return authors
  }

  const year = paper.year
  const page = paper.page
  let pageString = ''
  if (page) {
    pageString = `, ${page}`
  }
  if (year) {
    return `(${authors} ${year}${pageString})`
  }
  return `(${authors}${pageString})`
}

export const toChicagoAuthors = paper => {
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map((author, i) => {
    if (i === 0) {
      if (author?.authorName) {
        const nameParts = stripHtml(author?.authorName).split(' ')
        return `${nameParts[nameParts.length - 1]},  ${nameParts.slice(0, nameParts.length - 1).join(' ')}`
      }
      if (author?.family && author?.given) {
        return `${author?.family}, ${author?.given} `
      }
    }
    if (author?.family && author?.given) {
      return `${author?.given} ${author?.family}`
    }

    if (author?.authorName) {
      return author?.authorName
    }
    return ''
  })

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' and ')
  }

  if (authorStrings.length <= 7) {
    return authorStrings.slice(0, -1).join(', ') + ', and ' + authorStrings.slice(-1)[0]
  }

  return authorStrings.slice(0, 7).join(', ') + ' et al.'
}

export const toChicagoReference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toChicagoAuthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = ''

  if (authors) {
    result += authors
  }

  if (year) {
    result += `, ${year}`
  }

  if (title) {
    result += `. "${title}",`
  }

  if (journal) {
    result += ` ${journal}`
  }

  if (issue) {
    result += `(${issue})`
  }

  if (volume) {
    result += `, ${volume}`
  }

  if (page) {
    result += `:${page}`
  }

  result += '. https://doi.org/' + paper.doi
  return stripHtml(result)
}

const toVancouverInTextCitation = (paper, useParenthesis, referenceNumber) => {
  if (!paper) {
    return ''
  }

  if (useParenthesis) {
    return `${referenceNumber}`
  }

  let authors = toAPAAuthorsInText(paper)?.trim() || ''
  if (!authors) {
    authors = `"${paper.title}"`
  }
  if (authors) {
    return `${authors} ${referenceNumber}`
  }

  return `${referenceNumber}`
}

const toVancouverAuthors = paper => {
  // The author's name is listed as first initial, last name.
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map(author => {
    let family = stripHtml(author?.family)
    let initials = author?.given?.split(' ')
      .map(name => stripHtml(name).slice(0, 1) + '.')[0]

    if (family && initials) {
      return `${family} ${initials} `
    } else if (family) {
      return family
    }

    if (author?.authorName) {
      // grab last name from author formatted like "Domenic Rosati"
      const nameParts = stripHtml(author?.authorName).split(' ')
      if (nameParts.length > 1) {
        family = nameParts[nameParts.length - 1]
        // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
        if (nameParts[nameParts.length - 1] === '.') {
          family = nameParts[nameParts.length - 2]
        }
        initials = `${nameParts[0][0]}.`
        return `${family} ${initials}`
      }
    }

    return ''
  })?.filter(author => !!author)

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }

  if (authorStrings.length === 2) {
    return authorStrings.join(' and ')
  }

  if (authorStrings.length <= 6) {
    // join the last two authors with an ampersand
    return authorStrings.slice(0, -1).join(', ') + ', & ' + authorStrings.slice(-1)[0]
  }

  return authorStrings.slice(0, 6).join(', ') + 'et al.'
}

export const toVancouverReference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toVancouverAuthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = ''

  if (authors) {
    result += `${authors}.`
  }

  if (title) {
    result += ` ${title}.`
  }

  if (journal) {
    result += ` ${journal}`
  }

  if (year) {
    result += ` ${year}`
  }

  if (volume) {
    result += `;${volume}`
  }

  if (issue) {
    result += `(${issue})`
  }

  if (page) {
    result += `:${page}`
  }

  result += '. https://doi.org/' + paper.doi
  return stripHtml(result)
}

const toBibTexAuthors = paper => {
  if (!paper?.authors) {
    return
  }

  const authorStrings = paper?.authors.map(author => {
    let family = stripHtml(author?.family)
    let initials = author?.given?.split(' ')
      .map(name => stripHtml(name).slice(0, 1) + '.')
      .join(' ')

    if (family && initials) {
      return `${family}, ${initials}`
    } else if (family) {
      return family
    }

    if (author?.authorName) {
      // grab last name from author formatted like "Domenic Rosati"
      const nameParts = stripHtml(author?.authorName).split(' ')
      if (nameParts.length > 1) {
        family = nameParts[nameParts.length - 1]
        // if last name part is just a period (e.g. "Domenic Rosati .") then use the part before that
        if (nameParts[nameParts.length - 1] === '.') {
          family = nameParts[nameParts.length - 2]
        }
        initials = `${nameParts[0][0]}.`
        return `${family}, ${initials}`
      }
    }

    return ''
  })?.filter(author => !!author)

  if (!authorStrings || authorStrings.length === 0) {
    return
  }

  if (authorStrings.length === 1) {
    return authorStrings[0]
  }
  return authorStrings.join(' and ')
}

const toBibTexReference = paper => {
  if (!paper) {
    return ''
  }

  const authors = toBibTexAuthors(paper)?.trim() || ''
  const title = capitalize(paper.title || 'Untitled')
  const year = paper.year
  const journal = paper.journal || paper.shortJournal
  const issue = paper.issue
  const volume = paper.volume
  const page = paper.page

  let result = `@article{${paper.doi},\n`
  if (authors) {
    result += `  author = {${authors}},\n`
  }

  if (title) {
    result += `  title = {${title}},\n`
  }

  if (journal) {
    result += `  journal = {${journal}},\n`
  }

  if (year) {
    result += `  year = {${year}},\n`
  }

  if (volume) {
    result += `  volume = {${volume}},\n`
  }

  if (issue) {
    result += `  issue = {${issue}},\n`
  }

  if (page) {
    result += `  pages = {${page}},\n`
  }

  result += `  doi = {${paper.doi}}\n`
  result += '}'
  return stripHtml(result)
}

const toBibtexInTextCitation = (paper, useParenthesis) => {
  if (!paper || !paper?.doi) {
    return ''
  }
  if (useParenthesis) {
    return `\\citep{${paper.doi}}`
  }
  return `\\citet{${paper.doi}}`
}

export const generateInTextReference = ({
  reference,
  referenceNumber,
  citationStyle,
  useEndOfClauseCitationStyle
}) => {
  switch (citationStyle) {
    case 'harvard': // AFAICT this is identical to APA for the purposes of this function
    case 'apa':
      return toAPAInTextCitation(reference, useEndOfClauseCitationStyle)
    case 'mla':
      return toMLAInTextCitation(reference, useEndOfClauseCitationStyle)
    case 'chicago':
      return toChicagoInTextCitation(reference, useEndOfClauseCitationStyle)
    case 'vancouver':
      return toVancouverInTextCitation(reference, useEndOfClauseCitationStyle, referenceNumber)
    case 'bibtex':
      return toBibtexInTextCitation(reference, useEndOfClauseCitationStyle)
    case 'ieee':
    default:
      return toIEEEInTextCitation(reference, useEndOfClauseCitationStyle, referenceNumber)
  }
}

export const generateBibliography = ({
  references,
  citationStyle,
  useNumbers = true
}) => {
  let refFn = toAPAReference
  switch (citationStyle) {
    case 'harvard': // AFAICT this is identical to APA for the purposes of this function
    case 'apa':
      refFn = toAPAReference
      break
    case 'mla':
      refFn = toMLAreference
      break
    case 'chicago':
      refFn = toChicagoReference
      break
    case 'vancouver':
      refFn = toVancouverReference
      break
    case 'bibtex':
      refFn = toBibTexReference
      break
    case 'ieee':
    default:
      refFn = toIEEEreference
      break
  }

  const referencesText = references.map(
    (reference, i) => {
      const refString = `${refFn(reference.paper)}`
      if (!useNumbers) {
        return refString
      }
      if (citationStyle === 'ieee') {
        return `[${i + 1}] ${refString}`
      } else if (citationStyle === 'vancouver') {
        return `${i + 1}. ${refString}`
      } else {
        return `${refString}`
      }
    }
  )
  let copyString = ''
  if (['ieee', 'bibtex', 'vancouver'].includes(citationStyle)) {
    copyString += referencesText.join('\n')
  } else {
    copyString += referencesText.sort().join('\n')
  }
  return copyString
}

export const generateReference = ({
  reference,
  citationStyle
}) => {
  switch (citationStyle) {
    case 'harvard': // AFAICT this is identical to APA for the purposes of this function
    case 'apa':
      return toAPAReference(reference)
    case 'mla':
      return toMLAreference(reference)
    case 'chicago':
      return toChicagoReference(reference)
    case 'vancouver':
      return toVancouverReference(reference)
    case 'bibtex':
      return toBibTexReference(reference)
    case 'ieee':
    default:
      return toIEEEreference(reference)
  }
}
